import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * SEO Component — declarative <head> manager (no external dependency).
 *
 * Renders nothing. Imperatively syncs <title>, meta tags, canonical link,
 * <html lang>, and JSON-LD structured data on every prop change. All tags
 * managed by this component are tagged with `data-seo="managed"` so they
 * can be safely upserted without disturbing pre-rendered (index.html) tags.
 *
 * Props:
 *   - title          string                 page title
 *   - description    string                 meta description
 *   - keywords       string                 comma-separated keywords
 *   - image          string                 OG/Twitter image (relative or absolute)
 *   - url            string                 canonical URL (defaults to current route)
 *   - type           string                 OG type (default: "website")
 *   - locale         string                 OG locale (default: "en_US")
 *   - lang           string                 <html lang> (default: "en")
 *   - robots         string                 robots directive (default: "index, follow")
 *   - structuredData object | object[]      JSON-LD payload(s)
 */

const SITE = {
  baseUrl: 'https://www.codenticai.com',
  name: 'Codentic AI',
  twitterHandle: '@codenticai',
  defaultImage: '/og-image.png',
  defaultTitle: 'Codentic AI — Emerging World Models: A Memory Side-Car for Any AI Model',
  defaultDescription:
    'Emerging World Models (EWM) adds a structural memory and working-context layer beside any AI model — never a replacement for it. The host keeps its weights. Measured, content-addressed memory for on-premise and air-gapped deployments.',
  defaultKeywords:
    'Emerging World Models, EWM, world-model memory, AI memory layer, structural memory, AI side-car, JEPA, HLLSet algebra, content-addressed memory, on-premise AI, air-gapped AI, agent memory, auditable AI, NanoLM, AI Enterprise Suite, ROS 2 robotics'
};

const MANAGED_ATTR = 'data-seo';
const MANAGED_VAL = 'managed';

function SEO({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  locale = 'en_US',
  lang = 'en',
  robots = 'index, follow',
  structuredData = null
}) {
  const location = useLocation();

  const resolved = useMemo(() => {
    const t = title || SITE.defaultTitle;
    const d = description || SITE.defaultDescription;
    const k = keywords || SITE.defaultKeywords;
    const img = image || SITE.defaultImage;
    const fullUrl = url || `${SITE.baseUrl}${location.pathname}`;
    const fullImage = /^https?:\/\//i.test(img) ? img : `${SITE.baseUrl}${img}`;
    return { t, d, k, img: fullImage, fullUrl };
  }, [title, description, keywords, image, url, location.pathname]);

  // Stable key for structured data to avoid useEffect churn on object refs.
  const structuredDataKey = useMemo(
    () => (structuredData ? JSON.stringify(structuredData) : ''),
    [structuredData]
  );

  useEffect(() => {
    const { t, d, k, img, fullUrl } = resolved;

    // <html lang>
    if (typeof document !== 'undefined' && document.documentElement) {
      document.documentElement.setAttribute('lang', lang);
    }

    // <title>
    document.title = t;

    // Standard meta
    upsertMeta('name', 'description', d);
    upsertMeta('name', 'keywords', k);
    upsertMeta('name', 'author', SITE.name);
    upsertMeta('name', 'robots', robots);

    // Open Graph
    upsertMeta('property', 'og:title', t);
    upsertMeta('property', 'og:description', d);
    upsertMeta('property', 'og:image', img);
    upsertMeta('property', 'og:url', fullUrl);
    upsertMeta('property', 'og:type', type);
    upsertMeta('property', 'og:site_name', SITE.name);
    upsertMeta('property', 'og:locale', locale);

    // Twitter
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:site', SITE.twitterHandle);
    upsertMeta('name', 'twitter:title', t);
    upsertMeta('name', 'twitter:description', d);
    upsertMeta('name', 'twitter:image', img);

    // Canonical
    upsertLink('canonical', fullUrl);

    // JSON-LD — replace managed JSON-LD nodes only
    removeManagedJsonLd();
    if (structuredDataKey) {
      const payload = JSON.parse(structuredDataKey);
      const items = Array.isArray(payload) ? payload : [payload];
      items.forEach((item) => appendJsonLd(item));
    }
  }, [resolved, lang, robots, type, locale, structuredDataKey]);

  return null;
}

/* ----------------- helpers ----------------- */

function upsertMeta(attribute, key, content) {
  if (!content) return;
  const selector = `meta[${attribute}="${cssEscape(key)}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attribute, key);
    el.setAttribute(MANAGED_ATTR, MANAGED_VAL);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    el.setAttribute(MANAGED_ATTR, MANAGED_VAL);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function appendJsonLd(data) {
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute(MANAGED_ATTR, MANAGED_VAL);
  script.text = JSON.stringify(data);
  document.head.appendChild(script);
}

function removeManagedJsonLd() {
  document.head
    .querySelectorAll(`script[type="application/ld+json"][${MANAGED_ATTR}="${MANAGED_VAL}"]`)
    .forEach((n) => n.remove());
  // Cleanup any legacy JSON-LD added by older SEO impl (no marker attribute)
  document.head
    .querySelectorAll('script[type="application/ld+json"]:not([data-seo])')
    .forEach((n) => n.remove());
}

function cssEscape(value) {
  if (typeof window !== 'undefined' && window.CSS && window.CSS.escape) {
    return window.CSS.escape(value);
  }
  return String(value).replace(/(["\\])/g, '\\$1');
}

export default SEO;
